// AI maturity assessment dimensions (PRD §9 Stage 3).
// Content distilled from the engagement playbooks: NIST AI RMF (Govern/Map/Measure/Manage)
// plus the research corpus themes (AI maturity & org design, value management, agent/MCP
// governance, hallucination risk, AI SDLC & developer productivity, design-to-code).
//
// Score scale (anchors shown in the UI):
//   0 Absent · 1 Ad hoc · 2 Emerging · 3 Defined · 4 Managed · 5 Optimized

export interface AssessmentDimension {
  id: string;
  name: string;
  blurb: string;
  questions: string[];     // guided questionnaire prompts for the consultant/workshop
  lowSignals: string;      // what a 0–2 typically looks like
  highSignals: string;     // what a 4–5 typically looks like
  actions: string[];       // recommended actions when the dimension scores ≤2
  nistRefs: string[];      // related NIST AI RMF subcategories
  packageHint: string;     // which service package most directly remediates this
}

export const ASSESSMENT_DIMENSIONS: AssessmentDimension[] = [
  {
    id: 'strategy',
    name: 'Strategy & Executive Sponsorship',
    blurb: 'A funded AI ambition tied to business outcomes, with an accountable executive owner.',
    questions: [
      'Is there a written AI strategy linked to specific business goals and a funded budget line?',
      'Which executive personally owns AI outcomes, and how is progress reported to leadership?',
      'How are AI initiatives selected and stopped — is there a portfolio decision forum?',
    ],
    lowSignals: 'AI activity is bottom-up experiments; no budget owner; strategy is a slide, not a plan.',
    highSignals: 'Named executive sponsor, quarterly portfolio reviews, AI goals in business unit OKRs.',
    actions: [
      'Run an executive alignment workshop to agree 2–3 measurable AI ambitions.',
      'Name a single accountable AI sponsor and a portfolio decision cadence.',
      'Stand up a lightweight intake funnel so initiatives are chosen, not inherited.',
    ],
    nistRefs: ['GOVERN 1.3', 'GOVERN 2.3', 'MAP 1.3'],
    packageHint: 'readiness_sprint',
  },
  {
    id: 'organization',
    name: 'AI Organization & Operating Model',
    blurb: 'Clear roles, a hub/spoke or CoE pattern, and decision rights for AI work.',
    questions: [
      'Who designs, who approves, and who operates AI systems today — are those roles written down?',
      'Is there a central enablement function (CoE/platform team), or does every team solve AI alone?',
      'How do business, data, security, and engineering collaborate on an AI initiative?',
    ],
    lowSignals: 'Heroic individuals; every team picks its own tools; no shared patterns or review path.',
    highSignals: 'Defined operating model (hub-and-spoke/CoE), role charters, shared platform and patterns.',
    actions: [
      'Define an interim operating model: who proposes, approves, builds, and runs AI systems.',
      'Charter a small enablement group to own patterns, tooling, and reviews.',
      'Publish role descriptions for AI product owner, steward, and reviewer.',
    ],
    nistRefs: ['GOVERN 2.1', 'GOVERN 2.2', 'GOVERN 3.2'],
    packageHint: 'readiness_sprint',
  },
  {
    id: 'governance',
    name: 'Governance & Risk Management',
    blurb: 'Policies, AI inventory, risk tolerance, and review gates aligned to NIST AI RMF.',
    questions: [
      'Is there an approved AI/acceptable-use policy, and when was it last reviewed?',
      'Does an inventory of AI systems (including embedded/vendor AI) exist with owners?',
      'How are high-risk AI uses identified, approved, monitored, and decommissioned?',
    ],
    lowSignals: 'No policy or a generic IT policy; shadow AI everywhere; no inventory; no risk gates.',
    highSignals: 'NIST-mapped policy set, living inventory, risk-tiered approval gates, periodic reviews.',
    actions: [
      'Adopt an AI policy starter pack (acceptable use, data handling, human oversight).',
      'Build the AI system inventory with named owners and risk tiers.',
      'Define go/no-go gates for high-risk use cases with documented decisions.',
    ],
    nistRefs: ['GOVERN 1.1', 'GOVERN 1.2', 'GOVERN 1.4', 'GOVERN 1.5', 'GOVERN 1.6', 'GOVERN 1.7'],
    packageHint: 'governance_foundation',
  },
  {
    id: 'data',
    name: 'Data Readiness & Privacy',
    blurb: 'Data quality, access, classification, and privacy posture good enough for AI use.',
    questions: [
      'Is data classified (public/internal/confidential/restricted) and is that enforced in tools?',
      'Can teams reliably find, access, and trust the data an AI use case needs?',
      'How are privacy obligations (consent, retention, residency) applied to AI training/prompting?',
    ],
    lowSignals: 'Unclassified data lakes; copy-paste into chatbots; unclear what data may be used where.',
    highSignals: 'Enforced classification, governed access paths, privacy review built into AI intake.',
    actions: [
      'Stand up a pragmatic data classification scheme and apply it to AI-bound data first.',
      'Define which classifications may flow to which AI providers (contract + policy).',
      'Pick one valuable dataset and make it AI-ready end-to-end as the pattern.',
    ],
    nistRefs: ['MAP 1.6', 'MEASURE 2.10', 'GOVERN 1.2'],
    packageHint: 'governance_foundation',
  },
  {
    id: 'engineering',
    name: 'AI Engineering & Architecture',
    blurb: 'Reference architectures for RAG/LLM apps, model selection, and documented systems.',
    questions: [
      'Is there a reference architecture for LLM/RAG applications (retrieval, grounding, evals)?',
      'How are models selected, versioned, and swapped — is there a provider abstraction?',
      'Is application architecture documented well enough for AI systems to be reviewed?',
    ],
    lowSignals: 'One-off scripts call one vendor API; no evals; architecture lives in someone’s head.',
    highSignals: 'Reference patterns, provider abstraction, eval harnesses, architecture decision records.',
    actions: [
      'Adopt a reference RAG/agent architecture with explicit grounding and eval stages.',
      'Introduce architecture decision records (ADRs) for AI systems.',
      'Add hallucination countermeasures: retrieval grounding, structured outputs, evals.',
    ],
    nistRefs: ['MAP 2.3', 'MEASURE 2.5', 'MEASURE 2.9'],
    packageHint: 'engineering_architecture',
  },
  {
    id: 'agents',
    name: 'Agent, Tool & MCP Governance',
    blurb: 'Agentic AI, tool registries, MCP servers, permissions, and human approval gates.',
    questions: [
      'Which agents/copilots can take actions (not just answer), and what tools can they call?',
      'Is there a registry of MCP servers/tools with owners, scopes, and review status?',
      'Where are human approval gates required before an agent action takes effect?',
    ],
    lowSignals: 'Agents adopted ad hoc; tools granted broad credentials; no audit of agent actions.',
    highSignals: 'Tool/MCP registry, least-privilege scopes, approval gates, audited agent actions.',
    actions: [
      'Inventory agentic surfaces (IDE agents, copilots, automations) and the tools they reach.',
      'Stand up an MCP/tool registry with scopes, owners, and an approval workflow.',
      'Define human-in-the-loop gates for consequential actions and log agent activity.',
    ],
    nistRefs: ['GOVERN 6.1', 'GOVERN 6.2', 'MANAGE 3.1', 'MANAGE 4.1'],
    packageHint: 'engineering_architecture',
  },
  {
    id: 'security',
    name: 'Security & Resilience',
    blurb: 'AI-specific threats (prompt injection, data leakage, model abuse) and incident response.',
    questions: [
      'Have AI-specific threats (prompt injection, exfiltration via tools, jailbreaks) been assessed?',
      'Are secrets, credentials, and data boundaries enforced for AI integrations?',
      'Is there an AI incident response path, including rollback/deactivation of a misbehaving system?',
    ],
    lowSignals: 'AI apps shipped without threat modeling; shared API keys; no kill switch.',
    highSignals: 'Threat-modeled AI surfaces, scoped credentials, tested rollback and incident runbooks.',
    actions: [
      'Threat-model the top AI surfaces against prompt injection and tool abuse.',
      'Scope credentials per integration; remove long-lived shared keys.',
      'Write the AI incident runbook including deactivation and comms.',
    ],
    nistRefs: ['MEASURE 2.7', 'MANAGE 2.3', 'MANAGE 2.4', 'GOVERN 1.7'],
    packageHint: 'governance_foundation',
  },
  {
    id: 'sdlc',
    name: 'Developer Productivity & AI SDLC',
    blurb: 'AI coding assistants, code review discipline, and delivery-lifecycle integration.',
    questions: [
      'Which AI coding tools are sanctioned, and is usage measured beyond anecdotes?',
      'How is AI-generated code reviewed and tested before merge (and is that policy real)?',
      'Are design-to-code and documentation workflows using AI where it demonstrably helps?',
    ],
    lowSignals: 'Unsanctioned tool sprawl; AI code merged unreviewed; no productivity baseline.',
    highSignals: 'Sanctioned toolset, AI-aware review gates, measured cycle-time/quality impact.',
    actions: [
      'Sanction a primary AI coding toolset and define acceptable-use rules for it.',
      'Add AI-aware review/test gates (provenance, security review for generated code).',
      'Baseline delivery metrics now so AI impact is measurable, not anecdotal.',
    ],
    nistRefs: ['MAP 4.1', 'MEASURE 2.13', 'GOVERN 1.2'],
    packageHint: 'ai_sdlc',
  },
  {
    id: 'adoption',
    name: 'Change Management & Training',
    blurb: 'Role-based enablement, champions, and workflow redesign — not just tool licenses.',
    questions: [
      'Is training role-based (executive, builder, everyday user) or one generic course?',
      'Are workflows actually redesigned around AI, or is AI bolted onto old process?',
      'How is adoption measured, and who unblocks teams that stall?',
    ],
    lowSignals: 'Licenses issued, usage flat; fear or hype dominates; no champions network.',
    highSignals: 'Role-based curricula, champions in each function, adoption tracked and coached.',
    actions: [
      'Launch role-based enablement paths with hands-on, job-relevant exercises.',
      'Name champions per function and give them office hours + escalation paths.',
      'Pick two workflows to redesign end-to-end with AI and publicize the wins.',
    ],
    nistRefs: ['GOVERN 2.2', 'GOVERN 4.1', 'GOVERN 4.3'],
    packageHint: 'readiness_sprint',
  },
  {
    id: 'value',
    name: 'Value Management & Benefits Tracking',
    blurb: 'Business cases, KPI baselines, and realized-value tracking for AI investments.',
    questions: [
      'Do AI initiatives carry a business case with a measurable target (time, cost, revenue, risk)?',
      'Are baselines captured before pilots so impact can be proven afterward?',
      'Who reviews realized value and kills or scales initiatives accordingly?',
    ],
    lowSignals: 'Value claims are vibes; pilots never end; nothing is killed or scaled deliberately.',
    highSignals: 'KPI baselines, value reviews on a cadence, scale/kill decisions with evidence.',
    actions: [
      'Define a simple value model (hours saved, cycle time, revenue lift, risk reduction).',
      'Capture baselines for every pilot before it starts.',
      'Run a monthly value review that explicitly scales or kills initiatives.',
    ],
    nistRefs: ['MAP 1.4', 'MEASURE 1.2', 'MANAGE 1.1'],
    packageHint: 'automation_roadmap',
  },
];

export const DIMENSION_IDS = ASSESSMENT_DIMENSIONS.map((d) => d.id);

export function dimensionById(id: string): AssessmentDimension | undefined {
  return ASSESSMENT_DIMENSIONS.find((d) => d.id === id);
}

export const SCORE_ANCHORS: Record<number, string> = {
  0: 'Absent — not present at all',
  1: 'Ad hoc — individual heroics, nothing repeatable',
  2: 'Emerging — pockets of practice, undocumented',
  3: 'Defined — documented and followed in key areas',
  4: 'Managed — measured, reviewed, consistently applied',
  5: 'Optimized — continuously improved, business-embedded',
};
